import React from 'react';

import Container from '@amzn/awsui-components-console/container';
import FormField from '@amzn/awsui-components-console/form-field';
import Select, { SelectProps } from '@amzn/awsui-components-console/select';
import Box from '@amzn/awsui-components-console/box';
import { DateRangePickerProps } from '@amzn/awsui-components-console/date-range-picker';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Grid from '@amzn/awsui-components-console/grid';

import InfoTooltip from 'src/common/ui/InfoTooltip';
import { saveSearchHelpText } from 'src/common/savedSearches';
import { ShareSearchButton } from 'src/common/urlSharing';
import { trackStateToParams } from '../urlState';

import DateRangeFilter from './DateRangeFilter';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface TrackFiltersProps {
  namespace: SelectProps.Option | null;
  onNamespaceChange: (option: SelectProps.Option | null) => void;
  stringGroup: SelectProps.Option | null;
  onStringGroupChange: (option: SelectProps.Option | null) => void;
  dateRange: DateRangePickerProps.Value | null;
  onDateRangeChange: (value: DateRangePickerProps.Value | null) => void;
  onApply: () => void;
  /** Opens the naming dialog to persist the current filter selection. */
  onSaveSearch: () => void;
  namespaceOptions: SelectProps.Options;
  stringGroupOptions: SelectProps.Options;
  loading?: boolean;
  stringGroupsLoading?: boolean;
}

const TrackFilters: React.FC<TrackFiltersProps> = ({
  namespace,
  onNamespaceChange,
  stringGroup,
  onStringGroupChange,
  dateRange,
  onDateRangeChange,
  onApply,
  onSaveSearch,
  namespaceOptions,
  stringGroupOptions,
  loading,
  stringGroupsLoading,
}) => {
  const { t } = useTranslation();

  return (
    <Container>
      <Grid
        gridDefinition={[
          { colspan: { default: 12, s: 3 } },
          { colspan: { default: 12, s: 2 } },
          { colspan: { default: 12, s: 3 } },
          { colspan: { default: 12, s: 4 } },
        ]}
      >
        {/* Namespace selector — required before any data loads */}
        <FormField
          label={
            <span>
              {t('track-filter-namespace-label')}{' '}
              <Box variant="span" fontSize="body-s" color="text-status-error">
                {t('track-filter-required-suffix')}
              </Box>
            </span>
          }
        >
          <Select
            selectedOption={namespace}
            onChange={({ detail }) => onNamespaceChange(detail.selectedOption)}
            options={namespaceOptions}
            placeholder={t('track-filter-namespace-placeholder')}
            statusType={loading ? 'loading' : 'finished'}
            loadingText={t('track-filter-namespaces-loading')}
            filteringType="auto"
          />
        </FormField>

        {/* String group filter — disabled until a namespace is chosen */}
        <FormField label={t('track-column-string-group')}>
          <Select
            selectedOption={stringGroup}
            onChange={({ detail }) =>
              onStringGroupChange(detail.selectedOption)
            }
            options={stringGroupOptions}
            placeholder={t('track-filter-all')}
            disabled={!namespace}
            statusType={stringGroupsLoading ? 'loading' : 'finished'}
            loadingText={t('track-filter-string-groups-loading')}
            filteringType="auto"
          />
        </FormField>

        {/* Date range with 1-year max and no future dates */}
        <DateRangeFilter value={dateRange} onChange={onDateRangeChange} />

        {/* Actions share a single grid column so Search and Save Search sit
          directly next to each other, matching the track.html mockup.
          `top: l` padding bottom-aligns them with the filter inputs. */}
        <Box padding={{ top: 'l' }}>
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Button variant="primary" onClick={onApply} disabled={!namespace}>
              {t('search-submit')}
            </Button>
            {/* Save Search — help text shows on hover/focus of the button itself. */}
            <InfoTooltip content={saveSearchHelpText(t)} disabled={!namespace}>
              <Button
                iconName="star"
                onClick={onSaveSearch}
                disabled={!namespace}
                disabledReason={t('track-save-disabled-reason')}
              >
                {t('track-save-search')}
              </Button>
            </InfoTooltip>
            {/* Share search — disabled reason shows on hover while it is greyed out. */}
            <ShareSearchButton
              shareParams={trackStateToParams({
                namespace: namespace?.value ?? '',
                stringGroup: stringGroup?.value ?? '',
                dateRange,
              })}
              disabled={!namespace}
              disabledReason={t('track-share-disabled-reason')}
            />
          </SpaceBetween>
        </Box>
      </Grid>
    </Container>
  );
};

export default TrackFilters;
